import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { MapContainer } from '../../components/map/map-container';
import { MapContext } from '../../components/map/map-ctx';
import { OverviewMapContext } from './overview-map-provider';
import { useOverviewMapSources } from './overview-map-sources';

import { latLng } from 'leaflet';

const round = (n?: number, decimals = 3) => {
  return Number(Math.round(Number(n + 'e' + decimals)) + 'e-' + decimals);
};

const roundLatLng = (coords: L.LatLng, zoom = 1) => {
  return latLng(
    round(coords.lat, zoom > 17 ? 4 : 3),
    round(coords.lng, zoom > 17 ? 4 : 3)
  );
};

const areCoordsSame = (a?: L.LatLng, b?: L.LatLng): boolean => {
  if (!a || !b) {
    return false;
  }
  return a.lat === b.lat && a.lng === b.lng;
};

function OverviewMarkers() {
  const { map } = useContext(MapContext);

  const [, setRerender] = useState(0);
  const prevZoom = useRef(0);
  const prevCenter = useRef<L.LatLng | undefined>();
  const minZoom = 10;

  useEffect(() => {
    const currentMap = map.current?.ref;
    if (currentMap) {
      let timeoutId: NodeJS.Timeout;

      const debouncedRerender = (/* { target }: LeafletEvent */) => {
        const newZoom = currentMap.getZoom();
        const newCenter = roundLatLng(currentMap.getCenter(), newZoom);

        const updateRefs = () => {
          prevZoom.current = newZoom;
          prevCenter.current = newCenter;
        };

        if (newZoom < minZoom) {
          updateRefs();
          return;
        }
        const zoomOut = prevZoom.current > newZoom;

        if (areCoordsSame(newCenter, prevCenter?.current)) {
          // zoom in only
          if (!(newZoom >= minZoom && prevZoom.current < minZoom)) {
            // zoomOut .. we need to fetch data gain
            if (!(zoomOut && newZoom >= minZoom)) {
              updateRefs();
              return;
            }
          }
        }
        updateRefs();

        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setRerender((p) => p + 1);
        }, 500); // 300ms debounce delay
      };

      currentMap.addEventListener('moveend', debouncedRerender);

      return () => {
        currentMap.removeEventListener('moveend', debouncedRerender);
        clearTimeout(timeoutId); // Cleanup timeout on unmount
      };
    }
  }, [map, minZoom]);

  const {
    source,
    irzFacilityPopupConfig,
    ovzFacilityPopupConfig,
    productionSitePopupConfig,
  } = useContext(OverviewMapContext);

  useOverviewMapSources({
    minimumZoomToLoad: minZoom,
    map,
    source,
    popupConfigs: {
      IRZ_FACILITY: irzFacilityPopupConfig,
      OVZ_FACILITY: ovzFacilityPopupConfig,
      PRODUCTION_SITE: productionSitePopupConfig,
    },
  });

  return null;
}

export function Map() {
  const { source } = useContext(OverviewMapContext);

  const MemoizedMap = useMemo(
    () =>
      function Map() {
        return (
          <MapContainer
            height="calc(100vh - 282px)"
            showSuggest
            defaultZoom={10}
          >
            <OverviewMarkers />
          </MapContainer>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source]
  );

  return <MemoizedMap />;
}
